import { useEffect, useState } from "react";
import {
  ApiError,
  readAuditPersona,
  saveAuditPersona,
  type AuditPersona as Persona,
} from "../api/client";
import { Callout } from "../components/Callout";
import { TextAreaField } from "../components/TextAreaField";
import { TextField } from "../components/TextField";

/**
 * Screen 73's persona card — the words Complao conducts an audit in, and the words it does not.
 *
 * **The card is a version, not a form.** Save appends: every publish writes a whole new
 * snapshot with the note beside it, and no row is ever amended or removed. That is why the
 * note is required and why it carries the same ten-character floor as the four regulatory
 * reasons — the person who reads it is an accreditation assessor asking why an audit was
 * conducted the way it was, not the administrator who typed it.
 *
 * **Two blocks are shown and cannot be edited, and showing them is the point.** The preamble
 * and the invariants are composed into every prompt from code. A card that rendered only the
 * thirteen editable sections would read as though the thirteen were the whole instruction,
 * which is exactly the misreading that makes emptying one look harmless. Each locked block
 * says why it is locked, in the same place it is refused.
 *
 * **Emptying a section is allowed and is shown as what it is.** The section drops out of the
 * composed prompt entirely rather than leaving a bare heading, and the row says *not in
 * force* — so an administrator who clears the Stage 1 section can see that they have, before
 * an audit is conducted without it.
 *
 * **Nothing here reaches an audit already running.** A session stamps `persona_version` when
 * it opens and composes every turn from that number, so a publish lands on the next session
 * to open — the same rule the follow-up cap above it already follows.
 *
 * The composed prompt and its length are the server's, never assembled here. An administrator
 * editing thirteen boxes cannot otherwise see the thing they are editing, and every turn of
 * every session pays for its length.
 */
export function AuditPersona() {
  const [persona, setPersona] = useState<Persona | null>(null);
  const [bodies, setBodies] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    readAuditPersona()
      .then(adopt)
      .catch((cause: unknown) =>
        setError(
          cause instanceof ApiError
            ? cause.message
            : "The persona could not be read.",
        ),
      );
  }, []);

  function adopt(next: Persona) {
    setPersona(next);
    setBodies(
      Object.fromEntries(
        next.sections.map((section) => [section.key, section.body]),
      ),
    );
    setNote("");
  }

  if (error && persona === null) {
    return (
      <div className="card">
        <h2>Audit persona</h2>
        <p className="problem" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (persona === null) {
    return <div className="card">Reading the audit persona&hellip;</div>;
  }

  const changed = persona.sections.filter(
    (section) => (bodies[section.key] ?? "") !== section.body,
  );
  const noteError =
    note.trim().length > 0 && note.trim().length < persona.minimum_note
      ? `At least ${persona.minimum_note} characters. This is what an assessor reads when they ask why an audit was conducted this way.`
      : undefined;

  const publish = async () => {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const next = await saveAuditPersona({
        sections: bodies,
        note: note.trim(),
      });
      adopt(next);
      setSaved(next.version);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "The version was not published.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="between" style={{ alignItems: "baseline" }}>
        <h2>Audit persona</h2>
        <span className="sub" style={{ fontSize: "11px" }}>
          Version {persona.version} &middot;{" "}
          {persona.updated_by
            ? `published by ${persona.updated_by}`
            : "seeded with the platform"}{" "}
          &middot; {stamp(persona.updated_at)}
        </span>
      </div>

      <p className="sub" style={{ fontSize: "11.5px", marginTop: "-4px" }}>
        How Complao conducts an audit &mdash; its register, what it does with
        the client&rsquo;s own documents, and how it responds when an answer is
        short. Thirteen sections, composed in this order into the instruction
        every session turn is generated under.{" "}
        <b>Publishing appends a version</b>; nothing is amended and nothing is
        removed. Complao administrators only.
      </p>

      <p className="sub" style={{ fontSize: "11.5px" }}>
        Version {persona.version} was{" "}
        {persona.updated_by ? "published" : "seeded"} with this note:{" "}
        <i>&ldquo;{persona.note}&rdquo;</i>
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {persona.sections.map((section) => {
        const body = bodies[section.key] ?? "";
        const dirty = body !== section.body;
        const expanded = open === section.key;
        return (
          <div
            key={section.key}
            className="seccard"
            style={{ marginBottom: "10px" }}
          >
            <div className="between" style={{ alignItems: "baseline" }}>
              <b>{section.title}</b>
              <span className="sub" style={{ fontSize: "11px" }}>
                {body.trim() === "" ? (
                  <span className="problem" style={{ fontSize: "11px" }}>
                    Not in force &mdash; omitted from the prompt
                  </span>
                ) : (
                  `${body.trim().length} characters`
                )}
                {dirty ? <b style={{ marginLeft: "10px" }}>Edited</b> : null}
                <button
                  type="button"
                  className="btn sec sm"
                  style={{ marginLeft: "10px" }}
                  onClick={() => setOpen(expanded ? null : section.key)}
                  aria-expanded={expanded}
                >
                  {expanded ? "Close" : "Edit"}
                </button>
              </span>
            </div>
            <p className="sub" style={{ fontSize: "11.5px", marginTop: "4px" }}>
              {section.guidance}
            </p>
            {expanded ? (
              <>
                <TextAreaField
                  id={`persona-${section.key}`}
                  label={section.title}
                  value={body}
                  onChange={(value) =>
                    setBodies((current) => ({
                      ...current,
                      [section.key]: value,
                    }))
                  }
                  rows={12}
                  hint="Blank lines, `- ` bullets and **bold** only — the chat panel renders nothing else."
                />
                {dirty ? (
                  <div className="row">
                    <button
                      type="button"
                      className="btn sec sm"
                      onClick={() =>
                        setBodies((current) => ({
                          ...current,
                          [section.key]: section.body,
                        }))
                      }
                    >
                      Revert to version {persona.version}
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <pre className="plan-ed" style={PREVIEW}>
                {body.trim() === "" ? "— empty —" : body}
              </pre>
            )}
          </div>
        );
      })}

      {/* Composed around the sections and never out of the table. Shown because a card that
          rendered only the editable thirteen would read as though they were the whole
          instruction — which is what makes deleting one look harmless. */}
      <h3 style={{ fontSize: "13px", marginTop: "18px" }}>Not editable</h3>
      <p className="sub" style={{ fontSize: "11.5px", marginTop: "-4px" }}>
        Composed into every prompt around the sections above, and held in code
        rather than here. The same division the threshold floor makes: a
        stricter setting is an administrator&rsquo;s, and the clause underneath
        it is not.
      </p>
      {persona.invariants.map((invariant) => (
        <div
          key={invariant.key}
          className="seccard"
          style={{ marginBottom: "10px" }}
        >
          <div className="between" style={{ alignItems: "baseline" }}>
            <b>{invariant.title}</b>
            <span className="sub" style={{ fontSize: "11px" }}>
              Locked
            </span>
          </div>
          <p className="sub" style={{ fontSize: "11.5px", marginTop: "4px" }}>
            {invariant.reason}
          </p>
          <pre className="plan-ed" style={PREVIEW}>
            {invariant.body}
          </pre>
        </div>
      ))}

      <div className="row" style={{ marginTop: "12px" }}>
        <button
          type="button"
          className="btn sec sm"
          onClick={() => setShowPrompt(!showPrompt)}
          aria-expanded={showPrompt}
        >
          {showPrompt ? "Hide the composed prompt" : "Show the composed prompt"}
        </button>
        <button
          type="button"
          className="btn sec sm"
          onClick={() => setShowHistory(!showHistory)}
          aria-expanded={showHistory}
        >
          {showHistory
            ? "Hide the version history"
            : `Version history (${persona.history.length})`}
        </button>
        <span className="sub" style={{ fontSize: "11px", alignSelf: "center" }}>
          {persona.composed_characters.toLocaleString()} characters, sent with
          every turn of every session.
        </span>
      </div>

      {showPrompt ? (
        <pre className="plan-ed" style={{ ...PREVIEW, maxHeight: "460px" }}>
          {persona.composed}
        </pre>
      ) : null}

      {showHistory ? (
        <div className="tscroll" style={{ marginTop: "10px" }}>
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>Published</th>
                <th>By</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {persona.history.map((revision) => (
                <tr key={revision.version}>
                  <td>{revision.version}</td>
                  <td>{stamp(revision.created_at)}</td>
                  <td>{revision.created_by ?? "Seeded"}</td>
                  <td>{revision.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {saved !== null ? (
        <Callout tone="info" title={`Version ${saved} published.`}>
          It applies to the next session that opens. A session already running
          finishes under the version it stamped when it opened, and every
          transcript already written stays readable against the version it was
          written under.
        </Callout>
      ) : null}

      {changed.length > 0 ? (
        <>
          <Callout
            tone="warn"
            title={`${changed.length} section${changed.length === 1 ? "" : "s"} edited.`}
          >
            {changed.map((section) => section.title).join(", ")}. Publishing
            writes version {persona.version + 1}; version {persona.version} is
            retained and stays readable.
          </Callout>
          <div style={{ maxWidth: "620px" }}>
            <TextField
              id="persona-note"
              label="Why this changed"
              hint="Retained with the version, permanently."
              value={note}
              onChange={setNote}
              error={noteError}
              required
            />
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn sec"
              disabled={busy}
              onClick={() => persona && adopt(persona)}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="btn"
              disabled={
                busy ||
                Boolean(noteError) ||
                note.trim().length < persona.minimum_note
              }
              onClick={() => void publish()}
            >
              {busy ? "Publishing…" : `Publish version ${persona.version + 1}`}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Read-only section text. Wrapped, because a prompt is prose and not code. */
const PREVIEW = {
  fontSize: "11.5px",
  lineHeight: 1.6,
  whiteSpace: "pre-wrap" as const,
  overflowY: "auto" as const,
  maxHeight: "180px",
  marginTop: "8px",
};

function stamp(value: string): string {
  return value.slice(0, 16).replace("T", " ");
}
